/**
 * LocationFields - the town, the street, and the "Use my location" button
 * -----------------------------------------------------------------------
 * Used on both signup forms, in Settings, and on the chore form.
 *
 * WHY THE TOWN MATTERS MORE THAN IT LOOKS
 * The "within X km" filter needs coordinates. It used to be that only people
 * who pressed the button ever had any, so for everybody else every distance
 * on the site came out blank and the filter silently did nothing.
 *
 * Now the town is enough: the server knows where the towns around the school
 * are and fills the coordinates in. The button is still worth pressing - it
 * places you exactly rather than in the middle of your town.
 *
 * ONE BUTTON. It fills in the street and the town together; both can then be
 * corrected by hand, and a corrected street is looked up as soon as the box
 * is left - there is no second button to press.
 *
 * WHAT THE BUTTON DOES (since 17 September 2026)
 * It finds where the device is, then fills in the STREET there as well as the
 * town, in a box the person can change. That box is the point: where a phone
 * is right now is not always home - somebody signing up at school, a poster
 * at work - and a wrong guess has to be easy to see and to fix. Changing the
 * street and pressing "Find street" moves the pin to what they typed. Pressing
 * the location button again, after moving, replaces the street it filled in.
 *
 * The lookup happens on our own server from a copy of OpenStreetMap
 * (backend/utils/addresses.js). Nobody's position goes to a map company.
 *
 * THE STREET IS KEPT on the account, as the person's own address (models/
 * User.js). Nobody else is ever sent it. On the chore form the street is the
 * chore's own address instead, and is required.
 *
 * There is no postcode box. It was asked for, stored, and never used for
 * anything: distances come from coordinates, and nothing is posted to anybody.
 */

import { useEffect, useState } from 'react';
import { LocateFixed, Check } from 'lucide-react';

import { authApi, locationApi } from '../services/api';
import { useI18n } from '../hooks/useI18n';

/**
 * A position less sure than this (in metres) is not worth filling anything in
 * from. A phone falling back on wifi or the mobile network can be tens of
 * kilometres out - it once put somebody in Ispra 50 km away in Saronno - and
 * a town somebody has to notice and correct is worse than no town at all.
 */
const TOO_VAGUE_M = 2000;

/**
 * Where the device is RIGHT NOW - one attempt, as a promise.
 *
 * FIXED 17 September 2026, after the owner pressed the button, drove
 * somewhere else, pressed it again and got the first place back. This used
 * to accept a position up to a minute old, and phones happily hand back the
 * last one they had. Now nothing old is accepted (maximumAge 0) - and because
 * some phones (Safari on iPhone especially) still report the old position
 * first, this WATCHES for positions and takes the first one measured after
 * the button was pressed.
 *
 * `timestamp` is when the phone measured the position, on the same clock as
 * Date.now(). A value that is not a sensible date is taken as fresh rather
 * than waited on for ever.
 *
 * Rejects with the browser's own error, or a timeout shaped like one.
 */
function freshPosition(highAccuracy) {
  // High accuracy is what makes a house number possible on a phone. It can be
  // slow indoors, hence the rougher second attempt in useMyLocation.
  const waitMs = highAccuracy ? 15000 : 10000;

  return new Promise((resolve, reject) => {
    const pressedAt = Date.now();
    let finished = false;
    let watchId = null;

    const finish = (settle) => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      settle();
    };

    const timer = setTimeout(
      () => finish(() => reject({ code: 3, PERMISSION_DENIED: 1 })),
      waitMs
    );

    watchId = navigator.geolocation.watchPosition(
      (position) => {
        const measuredAt = position.timestamp;
        const sensible = Number.isFinite(measuredAt) && measuredAt > 1e12;
        // Two seconds' grace: a phone already tracking (a map app open) may
        // hand over a fix from just before the press, and that one is fine.
        if (!sensible || measuredAt >= pressedAt - 2000) finish(() => resolve(position));
      },
      (error) => finish(() => reject(error)),
      { enableHighAccuracy: highAccuracy, maximumAge: 0, timeout: waitMs }
    );
    if (finished) navigator.geolocation.clearWatch(watchId);
  });
}

/**
 * `disabled` switches the whole block off. Used when editing a chore that a
 * student has already agreed to: the address is one of the things they
 * weighed up before saying yes, so it stops being the poster's to change.
 * See routes/chores.js, which is where that is actually enforced.
 *
 * `addressDisabled` lets the street address stay open when everything else is
 * locked. That is for one case only: a booked chore from before the address
 * was required, which has none. Filling it in is not changing what the
 * student agreed to - and without it they have nowhere to go.
 *
 * The address is always required when it is shown (since 16 September 2026).
 * The server refuses a chore without one; the `required` here just says so
 * before the form is sent rather than after.
 */
export default function LocationFields({
  value,
  onChange,
  showAddress = false,
  required = true,
  disabled = false,
  addressDisabled = disabled,
}) {
  const { t } = useI18n();
  const [locating, setLocating] = useState(false);
  const [finding, setFinding] = useState(false);
  const [message, setMessage] = useState('');
  const [towns, setTowns] = useState([]);

  /**
   * The street text the pin was placed for, in this visit to the page. When
   * the box stops saying that, the pin is somewhere the box no longer
   * describes - so it is dropped, and the town is used until "Find street"
   * puts it back. Null when the pin did not come from this box (a saved
   * position).
   */
  const [pinnedTo, setPinnedTo] = useState(null);

  /**
   * True while the box holds a street a lookup placed the pin for - filled
   * in by the location button, or confirmed with Find - and nobody has edited
   * it since. When the button then finds somewhere else, that street
   * describes the OLD place and must go: leaving it is how the box once
   * stayed on the first place after a drive. A street typed and never looked
   * up is the person's own (a poster typing the job's address) and stays.
   */
  const [streetIsOurs, setStreetIsOurs] = useState(false);

  // The towns we know about, offered as suggestions. Typing one of these is
  // what gets somebody distances without sharing their exact position.
  useEffect(() => {
    authApi
      .towns()
      .then((d) => setTowns(d.towns || []))
      .catch(() => setTowns([]));
  }, []);

  const update = (patch) => onChange({ ...value, ...patch });

  const street = value.address || '';

  /** Sets the street box and anything else in one change, so neither is lost. */
  const setStreetAnd = (text, patch = {}) => update({ ...patch, address: text });

  const onStreetTyped = (text) => {
    setStreetIsOurs(false);
    // The pin belongs to what the box said when it was placed. Once the box
    // says something else, the town is used until "Find street" places it again.
    const pinGoesStale = pinnedTo !== null && text.trim() !== pinnedTo;
    if (pinGoesStale) setPinnedTo(null);
    setStreetAnd(text, pinGoesStale ? { lat: null, lng: null } : {});
  };

  const useMyLocation = async () => {
    if (!navigator.geolocation) {
      setMessage(t('location.noBrowser'));
      return;
    }

    setLocating(true);
    setMessage('');

    let position;
    try {
      position = await freshPosition(true);
    } catch (error) {
      if (error.code === error.PERMISSION_DENIED) {
        setLocating(false);
        setMessage(t('location.refused'));
        return;
      }
      // Too slow or no satellite fix - a rougher position is still useful.
      try {
        position = await freshPosition(false);
      } catch (again) {
        setLocating(false);
        setMessage(again.code === again.PERMISSION_DENIED ? t('location.refused') : t('location.failed'));
        return;
      }
    }

    const lat = Number(position.coords.latitude.toFixed(5));
    const lng = Number(position.coords.longitude.toFixed(5));
    const accuracy = Number(position.coords.accuracy);

    if (Number.isFinite(accuracy) && accuracy > TOO_VAGUE_M) {
      setLocating(false);
      setMessage(t('location.tooVague', { km: Math.max(1, Math.round(accuracy / 1000)) }));
      return;
    }

    let found = null;
    try {
      found = await locationApi.addressAt({ lat, lng });
    } catch {
      /* the coordinates on their own are still useful */
    }

    setLocating(false);

    const town = found?.town || null;
    // A street this form filled in described the LAST place found, so it goes
    // when a new one is found. One the person typed themselves stays.
    const address = found?.address || '';
    const text = address || (streetIsOurs ? '' : street);

    setStreetAnd(text, { lat, lng, ...(town ? { city: town } : {}) });
    setStreetIsOurs(Boolean(address));
    setPinnedTo(text.trim());

    if (!town && !address) setMessage(t('location.nothingHere'));
    else if (!address) setMessage(t('location.noStreet'));
    else setMessage('');
  };

  /**
   * Puts the pin on a street somebody typed. Runs when they leave the box,
   * not from a button of its own - two buttons next to each other, one of
   * which finds YOU and one of which finds a STREET, is one button too many.
   */
  const findStreet = async () => {
    const text = street.trim();
    if (text.length < 3) return;
    // Already where the box says: nothing to look up.
    if (pinnedTo !== null && text === pinnedTo) return;

    setFinding(true);
    setMessage('');
    try {
      const found = await locationApi.find({ address: text, town: value.city || '' });
      const town = found.town || value.city || '';
      // An exact match is written back tidied up ("via roma 14" becomes
      // "Via Roma 14"); anything less leaves what they typed alone.
      const newText = found.exact ? found.address : street;
      setStreetAnd(newText, { lat: found.lat, lng: found.lng, ...(town ? { city: town } : {}) });
      setPinnedTo(newText.trim());
      // The pin now belongs to this street. If the location button is pressed
      // later, somewhere else, this street describes the old pin and goes.
      setStreetIsOurs(true);
      setMessage('');
    } catch (err) {
      if (err.status === 404) {
        // The box names a street we cannot place, so whatever pin there was
        // belongs to somewhere else: back to the middle of the town.
        update({ lat: null, lng: null });
        setPinnedTo(null);
        setStreetIsOurs(false);
        setMessage(t('location.addressNotFound'));
      } else if (err.status === 429) {
        setMessage(t('location.tooMany'));
      } else {
        setMessage(t('location.findFailed'));
      }
    } finally {
      setFinding(false);
    }
  };

  const hasCoords = value.lat !== null && value.lat !== undefined;
  const streetLocked = showAddress ? addressDisabled : disabled;

  const streetBox = (
    <div className="field">
      <label htmlFor="address">
        {showAddress ? `${t('location.address')} *` : t('location.yourAddress')}
      </label>
      <input
        id="address"
        type="text"
        value={street}
        onChange={(event) => onStreetTyped(event.target.value)}
        onBlur={findStreet}
        onKeyDown={(event) => {
          // Enter puts the pin on what they typed rather than sending the
          // whole form half-filled.
          if (event.key === 'Enter') {
            event.preventDefault();
            event.target.blur();
          }
        }}
        placeholder="Via Roma 14"
        autoComplete="street-address"
        minLength={showAddress ? 3 : undefined}
        maxLength={200}
        disabled={streetLocked || finding}
        required={showAddress}
      />
      {showAddress && <span className="hint">{t('location.addressHint')}</span>}
      <span className="hint map-credit">
        {t('location.creditBefore')}{' '}
        <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">
          {t('location.creditLink')}
        </a>
      </span>
    </div>
  );

  return (
    <>
      {streetBox}

      <div className="field">
        <label htmlFor="city">{t('location.town')} {required && '*'}</label>
        <input
          id="city"
          type="text"
          list="known-towns"
          value={value.city || ''}
          onChange={(event) => update({ city: event.target.value })}
          placeholder="Varese"
          autoComplete="address-level2"
          disabled={disabled}
          required={required}
        />
        <datalist id="known-towns">
          {towns.map((t) => (
            <option key={t} value={t} />
          ))}
        </datalist>
        <span className="hint">
          {t('location.townHint')}
        </span>
      </div>

      <div className="field">
        <button
          type="button"
          className={`btn btn-sm btn-inline ${hasCoords ? 'btn-outline' : 'btn-outline-blue'}`}
          onClick={useMyLocation}
          disabled={locating || disabled}
          style={{ alignSelf: 'flex-start' }}
        >
          {hasCoords ? <Check size={16} /> : <LocateFixed size={16} />}
          {/* Once there is a position, the button says it can be pressed
              again. It used to say "Location saved", which read as a label
              rather than a button - so after a drive, people pressed Find instead. */}
          {locating ? t('location.findingYou') : hasCoords ? t('location.updatePrecise') : t('location.usePrecise')}
        </button>

        {message && <span className="hint" role="status">{message}</span>}
      </div>

    </>
  );
}
